import { encryptionService, EncryptionService } from './encryption-service'
import {
  supplierCredentialRepository,
  SupplierCredentialRepository,
} from '../repositories/supplier-credential-repository'
import { supplierAuditRepository } from '../repositories/supplier-log-repository'
import { supplierLogRepository } from '../repositories/supplier-log-repository'
import { supplierRepository } from '../repositories/supplier-repository'
import type {
  ConnectionTestResult,
  CredentialStatus,
  DecryptedCredentials,
  SupplierCredential,
  SupplierCredentialInput,
  SupplierCredentialView,
} from '../types'

/**
 * CredentialService — sole gateway for supplier secrets.
 * Connectors must call this service; never store credentials locally.
 */
export class CredentialService {
  private readonly credentials: SupplierCredentialRepository
  private readonly encryption: EncryptionService

  constructor(
    credentials: SupplierCredentialRepository = supplierCredentialRepository,
    encryption: EncryptionService = encryptionService,
  ) {
    this.credentials = credentials
    this.encryption = encryption
  }

  list(): SupplierCredential[] {
    return this.credentials.list()
  }

  getBySupplierId(supplierId: string): SupplierCredential | null {
    return this.credentials.findBySupplierId(supplierId)
  }

  getBySupplierCode(code: string): SupplierCredential | null {
    const supplier = supplierRepository.findByCode(code)
    if (!supplier) return null
    return this.credentials.findBySupplierId(supplier.id)
  }

  toView(credential: SupplierCredential | null): SupplierCredentialView | null {
    if (!credential) return null
    return {
      id: credential.id,
      supplierId: credential.supplierId,
      username: credential.username,
      passwordMasked: this.encryption.maskPassword(credential.encryptedPassword),
      hasPassword: Boolean(credential.encryptedPassword),
      apiKeyMasked: this.encryption.maskSecret(credential.apiKey),
      apiSecretMasked: this.encryption.maskSecret(credential.apiSecret),
      tokenMasked: this.encryption.maskSecret(credential.token),
      refreshTokenMasked: this.encryption.maskSecret(credential.refreshToken),
      expiresAt: credential.expiresAt,
      lastLogin: credential.lastLogin,
      status: credential.status,
      createdAt: credential.createdAt,
      updatedAt: credential.updatedAt,
    }
  }

  async save(
    supplierId: string,
    input: SupplierCredentialInput,
    changedBy = 'admin@masterhub.com',
  ): Promise<SupplierCredentialView> {
    const previous = this.credentials.findBySupplierId(supplierId)
    const saved = await this.credentials.upsert(supplierId, input)

    this.auditDiff(supplierId, previous, saved, changedBy, input)
    supplierLogRepository.add(supplierId, 'credential.saved', 'Credencial salva', 'success')

    return this.toView(saved)!
  }

  async decryptForConnector(supplierCode: string): Promise<DecryptedCredentials | null> {
    const credential = this.getBySupplierCode(supplierCode)
    if (!credential) return null

    return {
      username: credential.username,
      password: credential.encryptedPassword
        ? await this.encryption.decrypt(credential.encryptedPassword)
        : null,
      apiKey: credential.apiKey ? await this.encryption.decrypt(credential.apiKey) : null,
      apiSecret: credential.apiSecret ? await this.encryption.decrypt(credential.apiSecret) : null,
      token: credential.token ? await this.encryption.decrypt(credential.token) : null,
      refreshToken: credential.refreshToken
        ? await this.encryption.decrypt(credential.refreshToken)
        : null,
    }
  }

  async testConnection(supplierId: string): Promise<ConnectionTestResult> {
    const credential = this.credentials.findBySupplierId(supplierId)
    const testedAt = new Date().toISOString()

    if (!credential) {
      const result: ConnectionTestResult = {
        ok: false,
        status: 'DISCONNECTED',
        message: 'Nenhuma credencial cadastrada',
        testedAt,
      }
      supplierLogRepository.add(supplierId, 'connection.tested', result.message, 'error')
      return result
    }

    // Sprint 1.1 — simulated response (no real API yet)
    const hasAuth =
      Boolean(credential.encryptedPassword) ||
      Boolean(credential.apiKey) ||
      Boolean(credential.token)

    let status: CredentialStatus
    let message: string
    let ok: boolean

    if (!hasAuth || !credential.username) {
      status = 'INVALID_CREDENTIAL'
      message = 'Credencial inválida — usuário/senha ou token ausentes'
      ok = false
    } else if (credential.username.toLowerCase().includes('erro')) {
      status = 'ERROR'
      message = 'Erro na autenticação (simulado)'
      ok = false
    } else {
      status = 'CONNECTED'
      message = 'Conexão testada com sucesso (simulado)'
      ok = true
    }

    this.credentials.updateStatus(supplierId, status, ok ? testedAt : credential.lastLogin)
    supplierLogRepository.add(
      supplierId,
      'connection.tested',
      message,
      ok ? 'success' : 'error',
    )

    return { ok, status, message, testedAt }
  }

  async ensureSeedCredentials(): Promise<void> {
    const suppliers = supplierRepository.list()
    for (const [index, supplier] of suppliers.entries()) {
      if (this.credentials.findBySupplierId(supplier.id)) continue
      const statuses: CredentialStatus[] = [
        'CONNECTED',
        'DISCONNECTED',
        'ERROR',
        'INVALID_CREDENTIAL',
      ]
      const status = statuses[index % statuses.length]!
      await this.credentials.upsert(supplier.id, {
        username: `user_${supplier.code}`,
        password: `secret-${supplier.code}-pass`,
        apiKey: supplier.supportsApi ? `key-${supplier.code}` : null,
        apiSecret: supplier.supportsApi ? `sec-${supplier.code}` : null,
        token: supplier.supportsAutomation ? `tok-${supplier.code}` : null,
        refreshToken: supplier.supportsAutomation ? `ref-${supplier.code}` : null,
      })
      this.credentials.updateStatus(
        supplier.id,
        status,
        status === 'CONNECTED' ? new Date().toISOString() : null,
      )
    }
  }

  private auditDiff(
    supplierId: string,
    previous: SupplierCredential | null,
    next: SupplierCredential,
    changedBy: string,
    input: SupplierCredentialInput,
  ): void {
    const track = (field: string, before: string, after: string) => {
      if (before === after) return
      supplierAuditRepository.add(supplierId, field, before, after, changedBy)
    }

    track('username', previous?.username ?? '', next.username ?? '')
    if (input.password) track('password', previous?.encryptedPassword ? '[encrypted]' : '', '[encrypted]')
    if (input.apiKey) track('apiKey', previous?.apiKey ? '[encrypted]' : '', '[encrypted]')
    if (input.apiSecret) track('apiSecret', previous?.apiSecret ? '[encrypted]' : '', '[encrypted]')
    if (input.token) track('token', previous?.token ? '[encrypted]' : '', '[encrypted]')
    if (input.refreshToken) {
      track('refreshToken', previous?.refreshToken ? '[encrypted]' : '', '[encrypted]')
    }
  }
}

export const credentialService = new CredentialService()
